import { pbRequest, pbRequestVoid } from '~/services/request';
import {
    GIFT_UNAVAILABLE_REASONS,
    type LicenseGiftClaim,
    type LicenseGiftInvitation,
    type LicenseGiftInvitationInput,
    type LicenseGiftsOverview,
    type LicenseGiftShareLink,
    type LicenseGiftUnavailableReason,
    type PublicLicenseGift,
} from '~/types/person/license-gifts';
import { ApiError } from '~/utils/errors';

/**
 * Why app-api refuses a giver's own write, as the refusal's `reason`: sending an invitation or sending
 * one again, and turning the share link on. `first_name_required` refuses whatever would send a
 * message naming a giver whose account has no first name: an invitation, a resend, a share link.
 *
 * Two limits answer 429 with the wait in `Retry-After`: `invitation_resends_exceeded`, one invitation
 * sent again too often in an hour; and `address_invitations_exceeded`, one address's day of
 * invitations from every giver together, resends included.
 */
export const SPONSOR_REFUSALS = [
    'no_credits',
    'self_invite',
    'already_invited',
    'not_eligible',
    'simulated_operations',
    'first_name_required',
    'invitation_not_found',
    'invitation_not_pending',
    'invitation_resends_exceeded',
    'address_invitations_exceeded',
] as const;
export type SponsorRefusal = (typeof SPONSOR_REFUSALS)[number];

/** Why app-api refuses a claim, as the refusal's `reason`. `gift_unavailable` says why in its own field (`unavailableReason`). */
export const CLAIM_REFUSALS = ['gift_not_found', 'gift_unavailable', 'self_claim', 'simulated_operations', 'holder_has_live_license'] as const;
export type ClaimRefusal = (typeof CLAIM_REFUSALS)[number];

/** The refusal `error` carries when it is one of `known`; null for every other failure. */
function refusalIn<R extends string>(error: unknown, known: readonly R[]): R | null {
    if (!(error instanceof ApiError)) return null;

    const refused = error.reason;

    return known.find((reason) => reason === refused) ?? null;
}

/** Which of the giver's refusals a failed write is, or null when it is none of them. */
export const sponsorRefusal = (error: unknown): SponsorRefusal | null => refusalIn(error, SPONSOR_REFUSALS);

/** Which of the claim refusals a failed `claim` is, or null when it is none of them. */
export const claimRefusal = (error: unknown): ClaimRefusal | null => refusalIn(error, CLAIM_REFUSALS);

/** Why a claim refused as `gift_unavailable` could not take the gift, from the refusal's `unavailable_reason`; null for any other failure. */
export function unavailableReason(error: unknown): LicenseGiftUnavailableReason | null {
    if (!(error instanceof ApiError) || error.reason !== 'gift_unavailable') return null;

    const named = error.envelopeDetails.unavailable_reason;

    return GIFT_UNAVAILABLE_REASONS.find((reason) => reason === named) ?? null;
}

/**
 * Whether a claim refused because its invitation was already accepted was accepted by the signed-in
 * member themselves, from the refusal's `claimed_by_you`. Only that refusal carries it.
 */
export function claimedByYou(error: unknown): boolean {
    return error instanceof ApiError && error.reason === 'gift_unavailable' && error.envelopeDetails.claimed_by_you === true;
}

/**
 * Free licenses over app-api. Every method is one request and keeps nothing: a refusal leaves as an
 * `ApiError` whose `reason` is one of the lists above, read with `sponsorRefusal` or `claimRefusal`.
 */
export const licenseGiftsService = {
    /**
     * The signed-in member's free licenses: what they can give, what they gave, and who covers them.
     * `timeout` bounds a read a step waits on, in milliseconds; without it the shared bound applies.
     */
    async overview(options: { timeout?: number } = {}): Promise<LicenseGiftsOverview> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<LicenseGiftsOverview>(nuxtApp.$api.routes.licenseGifts.list(), {
            method: 'GET',
            failure: "We couldn't load your free licenses",
            timeout: options.timeout,
        });
    },

    /**
     * Email an invitation. It holds one of the member's licenses until it is accepted or expires: a
     * member can't withdraw one, and only Privacy Bee staff can.
     */
    async invite(input: LicenseGiftInvitationInput): Promise<LicenseGiftInvitation> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<LicenseGiftInvitation>(nuxtApp.$api.routes.licenseGifts.invitations.create(), {
            method: 'POST',
            body: input,
            expect: 201,
            failure: "We couldn't send that invitation",
        });
    },

    /** Send a waiting invitation again, which starts its week again. */
    async resend(gift: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.licenseGifts.invitations.resend(gift), {
            method: 'POST',
            expect: 204,
            failure: "We couldn't send that invitation again",
        });
    },

    /** Turn the share link on, or replace it with a new one: the previous link stops working. */
    async openShareLink(): Promise<LicenseGiftShareLink> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<LicenseGiftShareLink>(nuxtApp.$api.routes.licenseGifts.shareLink(), {
            method: 'PUT',
            failure: "We couldn't make your share link",
        });
    },

    /** Turn the share link off. */
    async closeShareLink(): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.licenseGifts.shareLink(), {
            method: 'DELETE',
            expect: 204,
            failure: "We couldn't turn off your share link",
        });
    },

    /**
     * Accept a gift as the signed-in member. `allowManagement` lets the giver help manage the
     * member's account, and the API reads it on an emailed invitation only.
     */
    async claim(token: string, allowManagement: boolean): Promise<LicenseGiftClaim> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<LicenseGiftClaim>(nuxtApp.$api.routes.licenseGifts.claim(), {
            method: 'POST',
            body: { token, allow_management: allowManagement },
            expect: 201,
            failure: "We couldn't add this protection to your account",
        });
    },

    /** What a claim link offers, read without a session. */
    async publicGift(token: string): Promise<PublicLicenseGift> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<PublicLicenseGift>(nuxtApp.$api.routes.licenseGifts.public(token), {
            method: 'GET',
            failure: "We couldn't load this invitation",
        });
    },
};
